// src/pages/admin/FormDetails.jsx
import React, { useMemo, useState } from "react";
import { Button, Modal, Select, Switch, Table, Tooltip, Dropdown, Spin } from "antd";
import { LuTrash, LuPencil } from "react-icons/lu";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UserResponseModal from "../../../components/Modals/UserResponseModal";
import PersonImage from "../../../assets/images/Image (3).png";
import { LayoutGrid, List, PencilLine, Trash2 } from "lucide-react";
import {
  QuestionCards,
  ResponseCards,
} from "../../../components/common/FormCards";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import {
  useDeleteQuestionMutation,
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
  useGetFormResponsesQuery,
  useGetFormStatsQuery,
  useUpdateQuestionMutation,
} from "../../../store/api/apiSlice";
import {
  MenuOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import { useSelector } from "react-redux";
import moment from "moment";
import AddQuestionDropdown from "../../../components/Modals/AddQuestionDropdown";
import StatsPopover from "../Forms/StatsTooltip";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";

const TemplateFormDetails = () => {
  const [activeTab, setActiveTab] = useState("questions"); // 'questions' or 'responses'
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewType, setViewType] = useState("list");
  const { state: locationState } = useLocation();
  const { id: formId } = useParams();
  const navigate = useNavigate();
  const basePath = window.location.pathname.split("/")[1];
  const { data: questionsData, isLoading: isLoading } =
    useGetFormQuestionsQuery(formId);
  const userRole = useSelector(selectCurrentRole);

  const {
    data: formDetailsData,
    isLoading: formDetailsLoading,
    isFetching: formDetailsFetching,
  } = useGetFormDetailsQuery(formId);
  const formData = locationState || formDetailsData?.data;
  const questions = questionsData?.data || [];
  const { data: responsesData, isLoading: responsesLoading } =
    useGetFormResponsesQuery(formId);
  const [deleteQuestion, { isLoading: isDeleting }] =
    useDeleteQuestionMutation();
  const [updateQuestion] = useUpdateQuestionMutation();

  const sortedQuestions = useMemo(() => {
    if (!questions?.length) return [];
    return [...questions]?.sort((a, b) => b.number - a.number);
  }, [questions]);

  const handleDragEnd = async (result) => {
    if (!result.destination || activeTab !== "questions") return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const items = Array.from(sortedQuestions);
    const [removed] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, removed);

    try {
      // Update all affected questions with new numbers
      const updates = items.map((question, index) => {
        if (question.number !== index) {
          return updateQuestion({
            formId,
            questionId: question._id,
            data: {
              ...question,
              number: index,
            },
          }).unwrap();
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
    } catch (error) {
      notification.error({
        message: "Failed to update question order",
        description:
          error.message || "An error occurred while updating question order",
      });
    }
  };

  const [loading, setLoading] = useState(false);
  const handleDeleteQuestion = (record) => {
    const modal = Modal.confirm({
      title: "Delete Question",
      content: "Are you sure you want to delete this question?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      confirmLoading: loading,
      onOk: async () => {
        setLoading(true);
        try {
          modal.destroy(); // Explicitly close the modal
          await deleteQuestion({
            formId,
            questionId: record._id,
          }).unwrap();
          // message.success('Question deleted successfully');
        } catch (error) {
          message.error(error.data?.message || "Failed to delete question");
          modal.destroy(); // Close modal even on error
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getQuestionActionsMenuItems = () => {
    const items = [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
      },
    ];

    if (userRole === "admin") {
      return [
        { key: "edit", label: "Edit", icon: <EditOutlined /> },
        ...items,
        {
          key: "delete",
          label: "Delete",
          danger: true,
          icon: <DeleteOutlined />,
        },
      ];
    }

    return items;
  };

  const getResponseActionsMenuItems = () => [
    {
      key: "view",
      label: "View Responses",
      icon: <EyeOutlined />,
    },
  ];

  const handleQuestionActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        navigate(
          `/${basePath}/forms/${formId}/questions/edit/${record._id}`,
        );
        break;
      case "view":
        navigate(
          `/${basePath}/forms/${formId}/questions/details/${record._id}`,
        );
        break;
      case "delete":
        handleDeleteQuestion(record);
        break;
      default:
        break;
    }
  };

  const handleResponseActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    if (actionKey === "view") {
      setSelectedUser(record);
      setResponseModalVisible(true);
    }
  };

  const renderActionsDropdown = (record, type = "question") => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items:
            type === "response"
              ? getResponseActionsMenuItems()
              : getQuestionActionsMenuItems(),
          onClick: (event) =>
            type === "response"
              ? handleResponseActionMenuClick(record, event.key, event)
              : handleQuestionActionMenuClick(record, event.key, event),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${record?.title || type}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  if (!formData) {
    const stillLoadingForm =
      formDetailsLoading || (formDetailsFetching && !formDetailsData);

    if (stillLoadingForm) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Form not found
          </h2>
          <p className="text-gray-500 mt-2">
            The requested form could not be loaded.
          </p>
          <Button onClick={() => navigate("/admin/templates")} className="mt-4">
            Return to Templates
          </Button>
        </div>
      </div>
    );
  }

  const formatResponseData = (responses) => {
    if (!responses?.length) return [];

    return responses.map((responseGroup) => {
      try {
        // Extract user info from _id field
        const userInfo = responseGroup._id;
        const details = responseGroup.details;

        // Get the latest step from details array
        const latestResponse = details[details.length - 1];
        const totalQuestions = latestResponse.total_questions;
        const currentStep = latestResponse.step;

        // Try to parse the first response for anonymous status
        let isAnonymous = false;
        if (details[0]?.response) {
          try {
            const firstResponseData = JSON.parse(details[0].response);
            isAnonymous = firstResponseData.anonymous || false;
          } catch (e) {
            console.warn("Failed to parse first response:", e);
          }
        }

        // Calculate completion percentage
        const completionPercentage = Math.round(
          (currentStep / totalQuestions) * 100
        );

        return {
          key: userInfo._id,
          srNo: details[0]?.step || 1,
          name: isAnonymous ? "Anonymous User" : userInfo.full_name,
          email: isAnonymous ? "N/A" : userInfo.email,
          questions: `${currentStep}/${totalQuestions}`,
          responses: details.length.toString(),
          percentage: `${completionPercentage}%`,
          anonymous: isAnonymous ? "Yes" : "No",
          status: currentStep === totalQuestions ? "Completed" : "Incomplete",
          avatar: PersonImage,
          // Add raw details for modal
          details: details,
          total_questions: totalQuestions,
        };
      } catch (error) {
        console.warn("Error processing response:", error);
        return {
          key: `fallback-${responseGroup._id?._id}`,
          srNo: 1,
          name: "Anonymous User",
          email: "N/A",
          questions: "0/0",
          responses: "0",
          percentage: "0%",
          anonymous: "No",
          status: "Incomplete",
          avatar: PersonImage,
          details: [],
        };
      }
    });
  };

  const columns = [
    {
      title: "Sr#",
      dataIndex: "number",
      key: "number",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Question",
      key: "question",
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-xl">
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: "Responses",
      key: "responses",
      render: (_, record) => (
        <span>
          {record.responses || 0} ({record.percentage || "0%"})
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => renderActionsDropdown(record, "question"),
    },
  ];

  const handleViewInQuestion = (record) => {
    // Store response data in localStorage for question view
    const responseData = {
      formId,
      responses: record.details,
      userData: {
        name: record.name,
        email: record.email,
        isAnonymous: record.anonymous === "Yes",
      },
    };
    localStorage.setItem("viewResponseData", JSON.stringify(responseData));

    // Navigate to the first question or the last answered question
    const currentResponse = record.details[record.details.length - 1];
    const questionNumber = currentResponse?.step || 1;

    // Use the same URL structure but pass state with isViewMode
    navigate(`/${basePath}/question/${formId}/${questionNumber}`, {
      state: { isViewMode: true, responseData },
    });
  };

  const responseColumns = [
    {
      title: "Sr#",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <img
            src={PersonImage}
            alt={record.name}
            className="w-9 h-9 rounded-full"
          />
          <div>
            <div className="font-medium text-gray-900">{record.name}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Question",
      dataIndex: "questions",
      key: "questions",
      width: 80,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Responses",
      key: "responses",
      width: 120,
      render: (_, record) => (
        <span>
          {record.responses} ({record.percentage})
        </span>
      ),
    },
    {
      title: "Anonymous",
      dataIndex: "anonymous",
      key: "anonymous",
      width: 100,
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, record) => (
        <span
          className={`px-3 py-2 rounded-full text-sm ${
            record.status === "Completed"
              ? "bg-green text-white"
              : "border borderred text-red-600"
          }`}
        >
          {record.status}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => renderActionsDropdown(record, "response"),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Form Info */}
      <div className="bg-white rounded-xl border p-2 mt-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {formData?.title}
            </h2>
            <p className="text-gray-500 text-sm mt-1 ">
              {formData?.description}
            </p>
          </div>
          <span className="px-3 py-1 bg-yellow-300 text-yellow-800 rounded-full text-sm">
            {/* {formData?.status} */}
            <span
              className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${
                formData.status === "active"
                  ? "bg-white text-yellow-800"
                  : formData.status === "draft"
                  ? "bg-white text-black"
                  : "bg-white text-black border border-black "
              }`}
            >
              {formData.type}
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-2 text-sm text-gray-500">
          <div>
            <span>{formData?.organization?.name}</span>

            {/* <span>{formData?.organization.description}</span> */}
            {/* <span>{formData?.type}</span> */}
          </div>
          <div>
            <span></span>
          </div>
        </div>
      </div>

      {/* Questions/Responses Tabs */}
      <div className="bg-white rounded-xl ">
        <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
          <p
            className="text-black text-2xl font-bold bg-white"
            // onClick={() => handleTabChange('questions')}
            // className={`px-4 py-2 rounded-xl border-0 text-sm ${
            //   activeTab === 'questions' ? 'bg-gray-900 text-white' : ''
            // }`}
          >
            Questions
          </p>

          <div className="flex items-center gap-2">
            <div className="flex bg-secondary rounded-xl p-1.5">
              <button
                type="button"
                className={`view-toggle-btn ${
                  viewType === "list" ? "bg-primary text-white" : ""
                }`}
                onClick={() => setViewType("list")}
              >
                <List size={20} />
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${
                  viewType !== "list" ? "bg-primary text-white" : ""
                }`}
                onClick={() => setViewType("grid")}
              >
                <LayoutGrid size={20} />
              </button>
            </div>
            {
              userRole === "admin" && (
                <button
                  className="btnSecondary border-0 h-9 px-4"
                  onClick={() =>
                    navigate(
                      `/${basePath}/template-form/${formId}/questions/add`
                    )
                  }
                >
                  + Add Question
                </button>
              )
              //  <AddQuestionDropdown
              //  onAddNewQuestion={() => navigate(`/${basePath}/forms/${formId}/questions/add`)}
              // />
            }
          </div>
        </div>

        {viewType === "list" && activeTab === "questions" ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-questions">
              {(provided) => (
                <div className="ant-table ant-table-fixed-header">
                  <div className="ant-table-container">
                    <div className="ant-table-content">
                      <table
                        style={{
                          tableLayout: "fixed",
                          width: "100%",
                        }}
                      >
                        <colgroup>
                          <col
                            style={{
                              width: "80px",
                            }}
                          />
                          <col />
                          <col
                            style={{
                              width: "120px",
                            }}
                          />
                          <col
                            style={{
                              width: "250px",
                            }}
                          />
                        </colgroup>
                        <thead className="ant-table-thead">
                          <tr className="mb-2">
                            <th className="text-start">Sr#</th>
                            <th className="text-start">Question</th>
                            <th className="text-start">Responses</th>
                            <th className="text-start">Actions</th>
                          </tr>
                        </thead>
                        <tbody
                          className="ant-table-tbody"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {sortedQuestions.map((question, index) => (
                            <Draggable
                              key={question._id}
                              draggableId={question._id.toString()}
                              index={index}
                            >
                              {(dragProvided) => (
                                <tr
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  className="ant-table-row ant-table-row-level-0"
                                >
                                  <td className="ant-table-cell py-2">
                                    <div className="flex items-center gap-2">
                                      <div
                                        {...dragProvided.dragHandleProps}
                                        className="cursor-grab"
                                      >
                                        <MenuOutlined className="text-gray-400" />
                                      </div>
                                      <span>{index + 1}</span>
                                    </div>
                                  </td>
                                  <td className="ant-table-cell py-2 overflow-hidden">
                                    <div className="flex flex-col gap-1">
                                      <div className="font-medium text-gray-900 truncate">
                                        {question.title}
                                      </div>
                                      <div className="text-sm text-gray-500 truncate">
                                        {question.description}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="ant-table-cell py-2 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span>
                                        {question.count || 0} (
                                        {(question?.response_percentage &&
                                          `${question?.response_percentage?.toFixed(
                                            1
                                          )}%`) ||
                                          "0.0%"}
                                        )
                                      </span>
                                      <StatsPopover
                                        stats={
                                          question.response?.[0]
                                            ?.statsInPercentage ||
                                          question.response?.[0]
                                            .numberStatistics
                                        }
                                        question={question.response?.[0]}
                                      />
                                    </div>
                                  </td>

                                  <td className="ant-table-cell py-2 whitespace-nowrap">
                                    {renderActionsDropdown(question, "question")}
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : activeTab === "responses" ? (
          viewType === "list" ? (
            <Table
              loading={responsesLoading}
              columns={responseColumns}
              dataSource={formatResponseData(responsesData?.data)}
              pagination={false}
              className="responses-table"
              scroll={{ x: "100%" }}
            />
          ) : (
            <ResponseCards
              data={responsesData?.data}
              loading={responsesLoading}
              onViewResponses={(user) => {
                setSelectedUser(user);
                setResponseModalVisible(true);
              }}
            />
          )
        ) : viewType === "list" ? (
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={sortedQuestions}
            pagination={false}
            className="form-details-table"
            scroll={{ x: "100%" }}
          />
        ) : (
          <QuestionCards
            data={sortedQuestions}
            navigate={navigate}
            loading={isLoading}
          />
        )}
      </div>
      <UserResponseModal
        visible={responseModalVisible}
        onClose={() => setResponseModalVisible(false)}
        userData={selectedUser}
        userResponses={selectedUser?.details || []}
      />
    </div>
  );
};

export default TemplateFormDetails;
